import * as React from 'react';
import { reduxForm, InjectedFormProps, Field, getFormSyncErrors } from 'redux-form';

const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import {
	getPoll,
	submitCreatePoll,
	clearFormData,
	submitUpdatePoll,
	setPollStatus,
	setSelectedPollId,
	clearManagePollFormError,
} from 'redux/modules/polls/managepoll';
import { Modal, Button, ErrorSummary } from 'components';
import { required, maxLength } from 'helpers/validationattributes';
import { IManagePoll, IManagePollAction } from 'models/polls/managepoll';
import { IPoll } from 'models/polls/poll';
import { PollStatus } from 'models/enums/statuses';
import { IModalAction } from 'models/modal';
import * as c from 'models/constants';
import { IUserPreferences } from 'models/user/userpreferences';
import { PortalSelectMenu, PortalInputField } from 'components/FormControls';
import { PortalPageSection, ButtonsSection } from 'components/PortalStructure';
import { ILanguage } from 'models/common';

import { onCloseModalForm } from 'helpers/common';
require('./style.css');

interface IProps extends InjectedFormProps {
	userPreferences?: IUserPreferences;
	managePoll: IManagePoll;
	initialValues: IManagePoll;
	currentFormValues: IPoll;
	languageDomainValues: ILanguage[];
	dispatch?: any;
	showHeaderCloseButton?: boolean;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	submitFailed: boolean;
	handleSubmit: any;
	formErrors?: any;
	submit: (formName: string) => void;
	change: (field: string, value: any) => void;

	/* functions props */
	getPoll: (id: string) => IManagePollAction;
	submitCreatePoll: (poll: IPoll, approve?: boolean) => IManagePollAction;
	submitUpdatePoll: (poll: IPoll, approve?: boolean) => IManagePollAction;
	clearFormData: () => IManagePollAction;
	setSelectedPollId: (id: string) => IManagePollAction;
	setPollStatus: (id: string, status: PollStatus) => IManagePoll;
	clearManagePollFormError: () => void;
	t?: (key: string) => string;
	closeModal?: () => IModalAction;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
}

const maxLength255 = maxLength(255);

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ManagePollForm extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.approvePoll = this.approvePoll.bind(this);
		this.closeModal = this.closeModal.bind(this);
		this.mapErrorKeyToMessage = this.mapErrorKeyToMessage.bind(this);
	}

	public componentDidMount() {
		this.props.getPoll(this.props.managePoll.selectedPollId);
	}

	public componentWillUnmount() {
		this.props.setSelectedPollId(null);
		this.props.clearManagePollFormError();
	}

	public onSubmit(form: IPoll, isApproval: boolean) {
		if (this.props.managePoll.isEditMode) {
			this.props.submitUpdatePoll(form, isApproval);
		} else {
			this.props.submitCreatePoll(form, isApproval);
		}
	}

	private approvePoll() {
		this.props.setPollStatus(this.props.managePoll.poll.id, PollStatus.Approved);
	}

	private closeModal() {
		onCloseModalForm(this.props.dirty, this.props.showModal, this.props.closeModal, this.props.t);
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'questionText':
				return t('Polls_ManagePoll_Label_QuestionText');

			case 'languageKey':
				return t('Polls_ManagePoll_Label_Language');

			default:
				return key;
		}
	}

	/** --------------RENDER ----------------------- */
	public render() {
		const {
			pristine,
			submitting,
			handleSubmit,
			t,
			showHeaderCloseButton,
			languageDomainValues,
			formErrors,
			submitFailed,
		} = this.props;
		const { isEditMode, error, message } = this.props.managePoll;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error,
			message,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const title = isEditMode ? t('Polls_ManagePoll_Label_Edit') : t('Polls_ManagePoll_Label_New');

		let saveAsDraftButton: JSX.Element = null;
		let approveButton: JSX.Element = null;
		let isApproval = false;
		if (this.props.currentFormValues && this.props.currentFormValues.status === PollStatus.Draft) {
			isApproval = true;
			saveAsDraftButton = (
				<Button type="submit" usage="admin" disabled={pristine || submitting}>
					{t('BSPortal_Common_SaveAsDraft')}
				</Button>
			);
		} else if (this.props.currentFormValues && this.props.currentFormValues.status === PollStatus.InApproval) {
			isApproval = false;
			approveButton = (
				<Button usage="admin" onClick={this.approvePoll}>
					{t('BSPortal_Common_Approve')}
				</Button>
			);
		}

		return (
			<Modal
				modalClass="managePollsModal"
				title={title}
				showHeaderCloseButton={showHeaderCloseButton}
				closeModal={this.closeModal}
			>
				<form onSubmit={handleSubmit(this.onSubmit)}>
					<div className="profileError">
						<ErrorSummary {...errorSummaryProps} />
					</div>
					<PortalPageSection usage="admin">
						<Field
							name="questionText"
							component={PortalInputField}
							usage="admin"
							type="text"
							label={t('Polls_ManagePoll_Label_QuestionText')}
							validate={[required, maxLength255]}
						/>

						<Field
							name="languageKey"
							component={PortalSelectMenu}
							usage="admin"
							items={languageDomainValues}
							autoFocus={true}
							isRequired={true}
							validate={[required]}
							label={t('Polls_ManagePoll_Label_Language')}
							searchable={false}
						/>
					</PortalPageSection>
					<ButtonsSection>
						<div className="row">
							<div className="col-sm-6">
								<Button
									usage="admin"
									onClick={handleSubmit((values) => {
										this.onSubmit(values, isApproval);
									})}
									disabled={pristine || submitting}
								>
									{t('BSPortal_Common_Save')}
								</Button>
							</div>
							<div className="col-sm-6">
								{saveAsDraftButton}
								{approveButton}
							</div>
						</div>
					</ButtonsSection>
				</form>
			</Modal>
		);
	}
}

function mapStateToProps(state: IStore) {
	const pollData: IManagePoll = state.managePoll;

	let currentFormValues: IPoll = null;
	if (state.form[c.FORM_NAME_MANAGE_POLL] && state.form[c.FORM_NAME_MANAGE_POLL].values) {
		currentFormValues = state.form[c.FORM_NAME_MANAGE_POLL].values;
	} else {
		currentFormValues = pollData.poll;
	}

	return {
		userPreferences: state.userPreferences,
		managePoll: pollData,
		initialValues: pollData.poll,
		languageDomainValues: state.domainValues.languages,
		currentFormValues,
		formErrors: getFormSyncErrors(c.FORM_NAME_MANAGE_POLL)(state),
	};
}

let ManagePoll = reduxForm(
	{
		form: c.FORM_NAME_MANAGE_POLL,
		enableReinitialize: true,
	},
	mapStateToProps,
)(ManagePollForm);

ManagePoll = connect(mapStateToProps, (dispatch) => ({
	getPoll: (id: string) => dispatch(getPoll(id)),
	submitCreatePoll: (poll: IPoll, approve?: boolean) => dispatch(submitCreatePoll(poll, approve)),
	submitUpdatePoll: (poll: IPoll, approve?: boolean) => dispatch(submitUpdatePoll(poll, approve)),
	clearFormData: () => dispatch(clearFormData()),
	setSelectedPollId: (id: string) => dispatch(setSelectedPollId(id)),
	setPollStatus: (id: string, status: PollStatus) => dispatch(setPollStatus(id, status)),
	clearManagePollFormError: () => dispatch(clearManagePollFormError()),
}))(ManagePoll);

export { ManagePoll };
